import {Component, DestroyRef, inject, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {CardComponent} from '../../../../shared/components/card/card.component';
import {AsyncPipe, NgTemplateOutlet} from '@angular/common';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {
  selectFilteredNotificationsWithCurrentPage,
  selectIsNotificationsLoading,
  selectNotificationFilter,
  selectNotificationsTotalCount
} from '../../state/notification.selectors';
import {Store} from '@ngrx/store';
import {NotificationActions} from '../../state/notification.actions';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {PageEvent} from '@angular/material/paginator';
import {withResetPageOnOtherFilterChange} from '../../../../shared/utils/filter-utils';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, EMPTY, filter, map, take} from 'rxjs';
import {PaginationComponent} from '../../../../shared/components/pagination/pagination.component';
import {InputWrapperComponent} from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import {RouteConstants} from '../../../../shared/constants/route-constants';
import {NotificationItemRowComponent} from '../../components/notification-item-row/notification-item-row.component';
import {NotificationLevel} from '@birco/rainmesh-api';
import {LabelConverter} from '../../../../shared/converter/label.converter';
import {IconComponent} from '../../../../shared/icons/icon.component';
import {ActivatedRoute, Params} from '@angular/router';
import {NotificationFilter} from '../../state/notification.state';
import {ConfirmDialogComponent, ConfirmDialogData} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {FilterDialogComponent} from '../../../../shared/components/filter-dialog/filter-dialog.component';

@Component({
  selector: 'app-page-notifications',
  imports: [
    CardComponent,
    AsyncPipe,
    TranslatePipe,
    PaginationComponent,
    InputWrapperComponent,
    ReactiveFormsModule,
    NotificationItemRowComponent,
    IconComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './page-notifications.component.html',
  styleUrl: './page-notifications.component.scss'
})
export class PageNotificationsComponent implements OnInit {
  private _fb = inject(FormBuilder);
  private store = inject(Store);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private translateService = inject(TranslateService);

  private destroyRef = inject(DestroyRef);

  loading$ = this.store.select(selectIsNotificationsLoading);
  totalCount$ = this.store.select(selectNotificationsTotalCount);
  notificationList$ = this.store.select(selectFilteredNotificationsWithCurrentPage);
  hasVisibleNotifications$ = this.notificationList$.pipe(
    map(notifications => notifications.length > 0)
  );
  hasUnreadVisible$ = this.notificationList$.pipe(
    map(notifications => notifications.some(notification => !notification.readAt))
  );
  @ViewChild('filterFields', {static: true}) filterFieldsTemplate?: TemplateRef<unknown>;
  isFilterDialogOpen = false;

  filterForm: FormGroup
  filterOptions = {
    level: [
      {label: 'default.all', value: null},
      ...Object.entries(NotificationLevel).map(([key, value]) => ({
        label: LabelConverter.getNotificationLevelLabel(key as NotificationLevel),
        value: value
      }))
    ],
    readState: [
      {label: 'default.all', value: 'all'},
      {label: 'notification.filter.readState.unread', value: 'unread'}
    ]
  }

  constructor() {
    this.filterForm = this._fb.group({
      readState: ['unread'],
      level: [null],
      page: [null],
      pageSize: [null],
    });
  }

  onChangePage(p: PageEvent) {
    this.filterForm.controls['page'].patchValue(p.pageIndex);
  }

  ngOnInit() {
    this.filterForm.valueChanges.pipe(
      withResetPageOnOtherFilterChange(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((newFilter) => {
      this.store.dispatch(NotificationActions.public.updateNotificationFilter({filter: newFilter}));
    });

    this.store.select(selectNotificationFilter).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError(_ => EMPTY)
    ).subscribe((filter) => {
      this.filterForm.patchValue({...filter});
    });

    this.route.queryParams?.pipe(
      map(params => this.mapQueryParamsToFilter(params)),
      filter(queryFilter => Object.keys(queryFilter).length > 0),
      map((queryFilter) => {
        this.filterForm.patchValue({...queryFilter});
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  onMarkAsRead(id: string) {
    this.store.dispatch(NotificationActions.public.markAsRead({id: id}))
  }

  onMarkAllAsRead() {
    this.store.dispatch(NotificationActions.public.markAllAsRead());
  }

  onDeleteAll() {
    const dialogData: ConfirmDialogData = {
      title: this.translateService.instant('notification.deleteAll.title'),
      body: this.translateService.instant('notification.deleteAll.body'),
      confirmLabel: this.translateService.instant('default.delete'),
      cancelLabel: this.translateService.instant('default.cancel'),
      action: 'danger'
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed()
      .pipe(
        take(1),
        filter((result): result is true => result === true)
      )
      .subscribe(_ => {
        this.store.dispatch(NotificationActions.public.deleteAll());
      });
  }

  openFilterDialog() {
    if (!this.filterFieldsTemplate || this.isFilterDialogOpen) return;
    this.isFilterDialogOpen = true;

    const dialogRef = this.dialog.open(FilterDialogComponent, {
      width: 'min(34rem, calc(100vw - 2rem))',
      maxWidth: '100vw',
      data: {
        titleKey: 'default.filter',
        contentTemplate: this.filterFieldsTemplate,
      },
    });

    this.filterForm.valueChanges.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => dialogRef.close());

    dialogRef.afterClosed().pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.isFilterDialogOpen = false;
    });
  }

  private mapQueryParamsToFilter(params: Params): Partial<NotificationFilter> {
    const filter: Partial<NotificationFilter> = {};

    if (typeof params['level'] === 'string') {
      filter.level = params['level'] as NotificationLevel;
    }

    if (params['readState'] === 'all' || params['readState'] === 'unread') {
      filter.readState = params['readState'];
    } else if (typeof params['hideRead'] === 'string') {
      filter.readState = params['hideRead'] === 'true' ? 'unread' : 'all';
    }

    return filter;
  }


  protected readonly RouteConstants = RouteConstants;
}
